// Códigos de país para selectores de teléfono (perfil, alta de usuarios, onboarding de
// WhatsApp, registro). Cubre los 18 países ofrecibles (los que tienen site de ML) más los
// que ya estaban. Los prefijos telefónicos no cambian: son lo único de país que queda a
// mano — el resto (moneda, geografía) sale del catálogo.
export const COUNTRIES = [
  { code: '+598', flag: '🇺🇾', name: 'Uruguay',      iso: 'UY' },
  { code: '+54',  flag: '🇦🇷', name: 'Argentina',    iso: 'AR' },
  { code: '+55',  flag: '🇧🇷', name: 'Brasil',       iso: 'BR' },
  { code: '+56',  flag: '🇨🇱', name: 'Chile',        iso: 'CL' },
  { code: '+595', flag: '🇵🇾', name: 'Paraguay',     iso: 'PY' },
  { code: '+591', flag: '🇧🇴', name: 'Bolivia',      iso: 'BO' },
  { code: '+51',  flag: '🇵🇪', name: 'Perú',         iso: 'PE' },
  { code: '+57',  flag: '🇨🇴', name: 'Colombia',     iso: 'CO' },
  { code: '+58',  flag: '🇻🇪', name: 'Venezuela',    iso: 'VE' },
  { code: '+52',  flag: '🇲🇽', name: 'México',       iso: 'MX' },
  { code: '+593', flag: '🇪🇨', name: 'Ecuador',      iso: 'EC' },
  { code: '+503', flag: '🇸🇻', name: 'El Salvador',  iso: 'SV' },
  { code: '+502', flag: '🇬🇹', name: 'Guatemala',    iso: 'GT' },
  { code: '+504', flag: '🇭🇳', name: 'Honduras',     iso: 'HN' },
  { code: '+505', flag: '🇳🇮', name: 'Nicaragua',    iso: 'NI' },
  { code: '+506', flag: '🇨🇷', name: 'Costa Rica',   iso: 'CR' },
  { code: '+507', flag: '🇵🇦', name: 'Panamá',       iso: 'PA' },
  { code: '+1',   flag: '🇩🇴', name: 'Dominicana',   iso: 'DO' },
  { code: '+34',  flag: '🇪🇸', name: 'España',       iso: 'ES' },
  { code: '+1',   flag: '🇺🇸', name: 'EE.UU.',       iso: 'US' },
];

export const PREFIJO_POR_DEFECTO = '+598';

/**
 * Prefijo telefónico del país del tenant (ISO-2 → '+595'). Es lo que arranca seleccionado
 * en los selectores de teléfono: un agente de Paraguay no tiene por qué ver +598.
 */
export function codigoTelefonicoDeIso(iso?: string | null, fallback = PREFIJO_POR_DEFECTO): string {
  if (!iso) return fallback;
  return COUNTRIES.find((c) => c.iso === iso.toUpperCase())?.code ?? fallback;
}

/**
 * Separa un teléfono en código de país + número local. Default UY.
 *
 * Tolera lo que hay cargado de verdad: de 144 agentes activos, 29 tienen el teléfono
 * fuera del formato canónico —16 arrancan con 0 ("099…"), 5 traen "598" sin +, otros
 * con espacios o guiones—, porque durante mucho tiempo el alta fue un campo libre. Sin
 * limpiar, al abrir esas fichas el número volvía a guardarse peor de lo que estaba
 * (`+598` + `099…` = `+598099…`). (04/08/2026)
 */
export function parsePhone(full?: string, porDefecto: string = PREFIJO_POR_DEFECTO): { countryCode: string; local: string } {
  if (!full) return { countryCode: porDefecto, local: '' };
  const limpio = String(full).replace(/[^\d+]/g, '');
  if (!limpio) return { countryCode: porDefecto, local: '' };
  const normalized = limpio.startsWith('+') ? limpio : '+' + limpio;
  const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  for (const c of sorted) {
    if (normalized.startsWith(c.code)) {
      return { countryCode: c.code, local: sinCeroInicial(normalized.slice(c.code.length)) };
    }
  }
  // Sin código reconocible es un número nacional suelto ("099123456"): el país del tenant.
  return { countryCode: porDefecto, local: sinCeroInicial(limpio.replace(/^\+/, '')) };
}

/** El 0 del formato nacional no va en E.164: "099123456" → "99123456". */
const sinCeroInicial = (n: string) => n.replace(/^0+/, '');

/**
 * Arma el teléfono como se guarda: E.164 sin espacios ni separadores. Es el formato que
 * esperan WhatsApp, Gustavo y el ruteo de Phylax; cualquier otro los deja sin reconocer
 * al agente.
 */
export function toE164(countryCode: string, local: string): string {
  const n = sinCeroInicial(String(local ?? '').replace(/\D/g, ''));
  return n ? `${countryCode}${n}` : '';
}

/**
 * ¿El número está bien para Uruguay? Devuelve el problema, o null si está bien.
 *
 * Un teléfono uruguayo son 8 dígitos después del +598: celular `9XXXXXXX`, fijo
 * `2XXXXXXX` (Montevideo) o `4XXXXXXX` (interior). Sin validar el largo entraron cosas
 * como `+5986457876` (le falta un dígito) y `+59809962966809912521926225955` (varios
 * números pegados), que no se pueden arreglar solas después: hay que preguntarle al
 * agente cuál era. Mejor no dejarlas entrar. (04/08/2026)
 *
 * Es un aviso, no un bloqueo: los números de otros países no se validan, y un fijo es
 * legítimo aunque para WhatsApp no sirva.
 */
export function problemaTelefonoUy(countryCode: string, local: string): string | null {
  if (countryCode !== '+598') return null;
  const n = String(local ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (!n) return null;                                   // vacío: el teléfono es opcional
  if (n.length !== 8) return `Un teléfono uruguayo tiene 8 dígitos (van ${n.length}).`;
  if (!/^[249]/.test(n)) return 'Un número uruguayo empieza con 9 (celular), 2 o 4 (fijo).';
  if (!n.startsWith('9')) return 'Es un fijo: para WhatsApp y Gustavo hace falta un celular (empieza con 9).';
  return null;
}
